import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ProjectId, TaskIdV2 } from 'api/models';
import {
  ActionPanel,
  usePopoverActionRef,
} from 'components/molecules/action-panel';
import { toastService } from 'components/molecules/toast';
import { AssigneeField, DateField } from 'components/organisms/tasks/fields';
import { TaskUpdateRequest } from 'components/organisms/tasks/use-update-tasks';
import { RootState } from 'data';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import {
  TranslationKey,
  useTranslate,
  useTranslation,
} from 'hooks/use-translation';
import { CreatableEditableRefreshableContent, Empty } from 'stores/core';
import { FieldsStore, StatusField, TaskField } from 'stores/fields';
import { Task } from 'stores/tasks';
import { isChildNode } from 'utils/is-child-node';
import { queueMacrotask } from 'utils/macrotask';
import { TaskFieldValue } from '../patch-utils';
import { AddAssignee } from './add-assignee';
import { Delete } from './delete';
import { SetAssignee } from './set-assignee';
import { SetDate } from './set-date';
import { SetStatus } from './set-status';
import { ShiftDates } from './shift-dates';
import { canProceedToUpdate } from './utils';

export type OnChange = (field: TaskField, to: TaskFieldValue) => void;
export type OnUpdate = (requests: TaskUpdateRequest[]) => void;

interface Props {
  ids: string[];
  getLookup: () => Record<number, CreatableEditableRefreshableContent<Task>>;
  projectIds: ProjectId[];
  isPrivate: boolean;
  onUpdate: OnUpdate;
  onDelete: (ids: TaskIdV2[]) => void;
  onClose: () => void;
}

export const BulkEditor: FC<Props> = ({
  onUpdate,
  onDelete,
  onClose,
  ids,
  getLookup,
  projectIds,
  isPrivate,
}) => {
  const title = useTranslation('task_list.bulk_editor.title', {
    count: ids.length,
  });
  const translate = useTranslate();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const status = FieldsStore.getTaskStatusField(fieldsStore)[1] as StatusField;
  const assignee = FieldsStore.getTaskFieldByName('assignee')(
    fieldsStore,
  )[1] as AssigneeField;
  const startDate = FieldsStore.getTaskFieldByName('startDate')(
    fieldsStore,
  )[1] as DateField;
  const dueDate = FieldsStore.getTaskFieldByName('dueDate')(
    fieldsStore,
  )[1] as DateField;
  const allowed = isChildNode([
    'ant-dropdown',
    'ant-picker-dropdown',
    'ant-select-dropdown',
    'ant-popover-inner-content',
  ]);
  const setStatus = usePopoverActionRef();
  const setStartDate = usePopoverActionRef();
  const setDueDate = usePopoverActionRef();
  const updateField = useCallback(
    (field: TaskField, to: TaskFieldValue) => {
      queueMacrotask(() => {
        const request = ids
          .map((id) => getLookup()[id] ?? Empty())
          .map(([, x]) => x as unknown as Record<string, unknown>)
          .filter(Boolean)
          .map((record) => ({
            record,
            updates: [
              {
                field,
                from: record[field.fieldName] as unknown as TaskFieldValue,
                to,
              },
            ],
          }));
        const filteredRequest = request.filter(canProceedToUpdate);

        if (filteredRequest.length !== request.length) {
          toastService({
            title: translate('task_list.bulk_editor.failed.title'),
            message: translate(
              `task_list.bulk_editor.failed.${
                field.fieldName === 'startDate' ? 'start_date' : 'due_date'
              }` as TranslationKey,
              {
                count: request.length - filteredRequest.length,
              },
            ),
          }).error();

          return;
        }
        onUpdate(filteredRequest);
      });
    },
    [ids, onUpdate, getLookup, translate],
  );
  const onChange = useCallback(
    (field: TaskField, to: TaskFieldValue) => {
      setStatus.current?.hide();
      setStartDate.current?.hide();
      setDueDate.current?.hide();
      updateField(field, to);
    },
    [updateField, setStatus, setStartDate, setDueDate],
  );
  const resourceData = useProjectAsRBACResource(
    projectIds?.[0]?.[1] ?? undefined,
  );
  const { hasAccessToResource } = useWithAccess();
  const {
    hasAccessToUpdateStatus,
    hasAccessToDeleteTask,
    hasAccessToUpdateAssignees,
    hasAccessToUpdateDates,
  } = useMemo(
    () => ({
      hasAccessToUpdateStatus: hasAccessToResource({
        rule: 'TASK_STATUS:WRITE',
        resourceData,
      }),
      hasAccessToUpdateDates: hasAccessToResource({
        rule: 'TASK_DATES:WRITE',
        resourceData,
      }),
      hasAccessToUpdateAssignees: hasAccessToResource({
        rule: 'TASK_ASSIGNEE:WRITE',
        resourceData,
      }),
      hasAccessToDeleteTask: hasAccessToResource({
        rule: 'TASK:DELETE',
        resourceData,
      }),
    }),
    [hasAccessToResource, resourceData],
  );
  const canShowEditor =
    hasAccessToUpdateStatus ||
    hasAccessToDeleteTask ||
    hasAccessToUpdateAssignees ||
    hasAccessToUpdateDates;

  return (
    <ActionPanel
      visible={ids.length > 0 && canShowEditor}
      title={title}
      onClose={onClose}
    >
      {hasAccessToUpdateStatus ? (
        <SetStatus
          innerRef={setStatus}
          status={status}
          allowed={allowed}
          onChange={onChange}
          title={translate('task_list.bulk_editor.options.status')}
        />
      ) : null}
      {hasAccessToUpdateStatus && hasAccessToUpdateDates ? (
        <ActionPanel.Divider />
      ) : null}
      {hasAccessToUpdateDates ? (
        <>
          <ActionPanel.Label>
            {translate('task_list.bulk_editor.labels.dates')}
          </ActionPanel.Label>
          <SetDate
            innerRef={setStartDate}
            date={startDate}
            title={translate('task_list.bulk_editor.options.start_date')}
            allowed={allowed}
            onChange={onChange}
          />
          <SetDate
            innerRef={setDueDate}
            date={dueDate}
            title={translate('task_list.bulk_editor.options.due_date')}
            allowed={allowed}
            onChange={onChange}
          />
          <ShiftDates
            startDate={startDate}
            dueDate={dueDate}
            title={translate('task_list.bulk_editor.options.shift_dates')}
            ids={ids}
            getLookup={getLookup}
            allowed={allowed}
            onUpdate={onUpdate}
          />
        </>
      ) : null}
      {(hasAccessToUpdateStatus || hasAccessToUpdateDates) &&
      hasAccessToUpdateAssignees ? (
        <ActionPanel.Divider />
      ) : null}
      {hasAccessToUpdateAssignees ? (
        <>
          <ActionPanel.Label>
            {translate('task_list.bulk_editor.labels.people')}
          </ActionPanel.Label>
          <AddAssignee
            ids={ids}
            lookup={getLookup()}
            assignee={assignee}
            title={translate('task_list.bulk_editor.options.add_assignee')}
            allowed={allowed}
            onUpdate={onUpdate}
            isPrivate={isPrivate}
            projectIds={projectIds}
          />
          <SetAssignee
            assignee={assignee}
            title={translate('task_list.bulk_editor.options.assignee')}
            allowed={allowed}
            onChange={onChange}
            isPrivate={isPrivate}
            projectIds={projectIds}
          />
        </>
      ) : null}
      {(hasAccessToUpdateStatus ||
        hasAccessToUpdateDates ||
        hasAccessToUpdateAssignees) &&
      hasAccessToDeleteTask ? (
        <ActionPanel.Divider />
      ) : null}
      {hasAccessToDeleteTask ? (
        <Delete
          title={translate('task_list.bulk_editor.options.delete')}
          allowed={allowed}
          onDelete={onDelete}
          onClose={onClose}
          ids={ids}
        />
      ) : null}
    </ActionPanel>
  );
};
